'use client'
import React from 'react';
import Link from 'next/link';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/pagination';

import { useGetActualitesByCategory } from '@/actions/actualite';
import type { IActualite } from '@/types/actualite';

// ----------------------------------------------------------------------

interface NewsProps {
  /** Category slug sent to the API — section is hidden when omitted */
  categorySlug?: string;
  /** Display name shown in the section heading */
  categoryName?: string;
}

// ----------------------------------------------------------------------

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15C11.866 15 15 11.866 15 8ZM4.75 7.25C4.33579 7.25 4 7.58579 4 8C4 8.41421 4.33579 8.75 4.75 8.75L9.43934 8.75L8.21967 9.96967C7.92678 10.2626 7.92678 10.7374 8.21967 11.0303C8.51256 11.3232 8.98744 11.3232 9.28033 11.0303L11.7803 8.53033C12.0732 8.23744 12.0732 7.76256 11.7803 7.46967L9.28033 4.96967C8.98744 4.67678 8.51256 4.67678 8.21967 4.96967C7.92678 5.26256 7.92678 5.73744 8.21967 6.03033L9.43934 7.25L4.75 7.25Z"
      fill="#1B365E"
    />
  </svg>
);

const NewsCardSkeleton = () => (
  <div className="bg-[#FFE4B4] pt-6 md:pt-7 md:pr-6 pr-[14.46px] md:pl-8.5 pl-[27.22px] pb-5.75 rounded-2xl h-full animate-pulse">
    <div className="h-5 bg-[#e8c97a] rounded w-3/4 mb-4" />
    <div className="h-3 bg-[#e8c97a] rounded w-1/4 mb-4" />
    <div className="space-y-2 mb-10">
      <div className="h-3 bg-[#e8c97a] rounded w-full" />
      <div className="h-3 bg-[#e8c97a] rounded w-5/6" />
      <div className="h-3 bg-[#e8c97a] rounded w-4/6" />
    </div>
    <div className="h-4 bg-[#e8c97a] rounded w-1/3" />
  </div>
);

const NewsCard = ({ item, href }: { item: IActualite; href: string }) => (
  <div className="bg-[#FFE4B4] pt-6 md:pt-7 md:pr-6 pr-[14.46px] md:pl-8.5 pl-[27.22px] pb-5.75 rounded-2xl h-full">
    <h3 className="text-[#1B365E] font-semibold text-base leading-6.5 font-fustat">
      {item.title}
    </h3>

    <p className="text-[#1D1D1D] text-[8px] tracking-[3%] leading-5 mt-3.5 mb-2 md:mb-1.75 font-fustat">
      {item.published_at}
    </p>

    <p className="text-[#1B365E] text-sm leading-5 mb-10">
      {item.description}
    </p>

    <Link href={href} className="flex items-center gap-2 font-bold text-[#1B365E] text-[15px] transition-colors hover:text-[#2c5282]">
      <ArrowIcon />
      Lire l&apos;article
    </Link>
  </div>
);

// ----------------------------------------------------------------------

const News: React.FC<NewsProps> = ({ categorySlug, categoryName }) => {
  const { actualites, actualitesLoading, actualitesEmpty } = useGetActualitesByCategory(categorySlug);

  if (!actualitesLoading && actualitesEmpty) return null;

  const label = categoryName ?? categorySlug;

  return (
    <section className="px-4 md:px-5 pt-[19.7px] md:pt-11.5 pb-5 md:pb-20.5 bg-[#FFF7E9]">
      <div className="max-w-355 mx-auto w-full">
        <h2 className="mb-6 md:mb-8 md:text-left text-center font-red-hat text-[#1B365E] text-[30px] md:text-[40px] font-light leading-10 md:leading-13.25 tracking-[0.3px]">
          L&apos;actualité sur les formations {label && 'en'} {label ?? 'Spot Formation'}
        </h2>

        {/* Desktop / Tablet grid — hidden on mobile */}
        <div className="hidden lg:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {actualitesLoading
            ? Array.from({ length: 3 }).map((_, i) => <NewsCardSkeleton key={i} />)
            : actualites.slice(0, 3).map((item) => <NewsCard key={item.id} item={item} href={`/actualites/${item.type?.slug}/${item.slug}`} />)}
        </div>

        {/* Mobile Swiper — only visible on mobile */}
        <div className="lg:hidden">
          {actualitesLoading ? (
            <div className="grid grid-cols-1 gap-4">
              {Array.from({ length: 3 }).map((_, i) => <NewsCardSkeleton key={i} />)}
            </div>
          ) : (
            <Swiper
              modules={[Pagination]}
              spaceBetween={24}
              slidesPerView={1}
              pagination={{ clickable: true }}
              breakpoints={{
                0: { slidesPerView: 1, spaceBetween: 16 },
                768: { slidesPerView: 2, spaceBetween: 24 },
              }}
              className="pb-11!"
            >
              {actualites.map((item) => (
                <SwiperSlide key={item.id} className="pb-0">
                  <NewsCard item={item} href={`/actualites/${item.type?.slug}/${item.slug}`} />
                </SwiperSlide>
              ))}
            </Swiper>
          )}
        </div>
      </div>
    </section>
  );
};

export default News;
